/* eslint-disable indent */
import React, { useCallback, useEffect, useState } from "react";
import { TransitionGroup, CSSTransition } from "react-transition-group";
import { Election } from "../types";
import Slide from "./Slide";

interface SlideShowProps {
  results: Election[];
  children: JSX.Element[];
}

const SlideShow = ({ results, children }: SlideShowProps) => {
  const [slideNumber, setSlideNumber] = useState(0);
  const [slideContent, setSlideContent] = useState(<></>);
  const [activeNextSlideHandler, setActiveNextSlideHandler] = useState<
    null | (() => boolean)
  >(null);

  const registerNextSlideHandler = useCallback(
    (handler: (() => boolean) | null) => {
      if (handler) {
        setActiveNextSlideHandler(() => handler);
      } else {
        setActiveNextSlideHandler(null);
      }
    },
    [setActiveNextSlideHandler]
  );

  const keyUpHandler = useCallback(
    async (e: KeyboardEvent) => {
      if (e.defaultPrevented) return;
      switch (e.key) {
        case "Left": // IE/Edge specific value
        case "ArrowLeft":
          setActiveNextSlideHandler(null);
          setSlideNumber(Math.max(0, slideNumber - 1));
          break;
        case "Right": // IE/Edge specific value
        case "ArrowRight":
          // If a sub-slide has a handler
          if (activeNextSlideHandler) {
            if (activeNextSlideHandler() == false) {
              // If the handler returns false, it means the slide has ran out of slides
              registerNextSlideHandler(null);
              // So return control to the master
              setSlideNumber(Math.min(children.length - 1, slideNumber + 1));
            }
          } else {
            setSlideNumber(Math.min(children.length - 1, slideNumber + 1));
          }
          break;
        default:
          return;
      }

      e.preventDefault();
    },
    [
      slideNumber,
      setSlideNumber,
      activeNextSlideHandler,
      registerNextSlideHandler,
    ]
  );

  useEffect(() => {
    window.addEventListener("keyup", keyUpHandler);

    return () => window.removeEventListener("keyup", keyUpHandler);
  }, [keyUpHandler, activeNextSlideHandler]);

  useEffect(() => {
    const slide = children[slideNumber];
    setSlideContent(
      <Slide
        {...slide.props}
        results={results}
        registerNextSlideHandler={registerNextSlideHandler}
      />
    );
  }, [slideNumber]);

  return (
    <main className="slideshow">
      <TransitionGroup>
        <CSSTransition
          key={slideNumber}
          classNames="slide-transition"
          timeout={{ enter: 1000, exit: 1000 }}
        >
          {slideContent}
        </CSSTransition>
      </TransitionGroup>
    </main>
  );
};

export default SlideShow;
